import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import dayjs from 'dayjs';
import { Observable, of } from "rxjs";
import { shareReplay, tap, map, catchError } from "rxjs";
import { User } from "src/models/models";
import { BaseService } from "./base.service";
import { decodeAccessToken } from "./util";

@Injectable()
export class AuthService extends BaseService<User, User> {
  constructor(protected override http: HttpClient, private route: Router) {
    super(http, 'user');
  }

  confirmEmail(confirmationToken: string): Observable<any> {
    return this.get(`confirm/${confirmationToken}`);
  }

  login(email: string, password: string): Observable<any> {
    return this.http.post(this.createUrl('login'), { email, password })
      .pipe(
        tap(() => {
          localStorage.setItem('isLoggedIn', 'true');
        }),
        shareReplay()
      );
  }

  resetEmail(email: string): Observable<User> {
    return this.post('resetPassword', { email })
      .pipe(shareReplay());
  }

  resetPassword(password: string, resetString: string): Observable<User> {
    return this.post('changePassword', { password, resetString })
      .pipe(shareReplay());
  }

  register(user: User): Observable<User> {
    return this.post('register', user)
      .pipe(shareReplay());
  }

  logout() {
    this.http.post(this.createUrl('logout'), {}).subscribe({
      next: () => {
        localStorage.removeItem('isLoggedIn');
        localStorage.removeItem('id_token');
        this.route.navigateByUrl('/login');
      },
      error: () => {
        localStorage.removeItem('isLoggedIn');
        localStorage.removeItem('id_token');
        this.route.navigateByUrl('/login');
      }
    });
  }

  checkAuthStatus(): Observable<{ authenticated: boolean }> {
    return this.http.get<{ authenticated: boolean }>(this.createUrl('profile'))
      .pipe(
        tap(() => {
          localStorage.setItem('isLoggedIn', 'true');
        }),
        catchError(() => {
          localStorage.removeItem('isLoggedIn');
          return of({ authenticated: false });
        }),
        shareReplay()
      );
  }

  /**
   * Check if user is logged in by checking localStorage flag.
   * The flag is set on successful login/OAuth and cleared on logout.
   * The actual token validation happens via cookies on API calls.
   */
  public isLoggedIn(): boolean {
    const isLoggedInFlag = localStorage.getItem('isLoggedIn');
    return isLoggedInFlag === 'true';
  }

  /**
   * Force a fresh authentication check by calling the server.
   * Use this when you need to verify the actual auth state.
   */
  verifyAuth(): Observable<boolean> {
    return this.http.get<any>(this.createUrl('profile')).pipe(
      tap(() => {
        localStorage.setItem('isLoggedIn', 'true');
      }),
      map(() => true),
      catchError(() => {
        localStorage.removeItem('isLoggedIn');
        return of(false);
      }),
      shareReplay()
    );
  }

  getExpiration() {
    try {
      const token = localStorage.getItem("id_token");
      if (!token) {
        const isLoggedIn = localStorage.getItem('isLoggedIn');
        if (isLoggedIn === 'true') {
          return dayjs().add(1, 'day');
        }
        return null;
      }
      const tokenInfo = decodeAccessToken(token);
      return tokenInfo?.exp ? dayjs.unix(tokenInfo.exp) : null;
    } catch {
      return null;
    }
  }

  isLoggedOut(): boolean {
    return !this.isLoggedIn();
  }

  isAdmin(): Promise<boolean> {
    return this.http.get<{ isAdmin: boolean }>(this.createUrl('profile'))
      .pipe(
        shareReplay()
      )
      .toPromise()
      .then(response => response?.isAdmin || false)
      .catch(() => false);
  }

  getUserName(): Observable<string | null> {
    return this.http.get<any>(this.createUrl('profile'))
      .pipe(
        shareReplay(),
        map((user: any) => user.firstName || null)
      );
  }

  getUser(): Observable<User | null> {
    return this.http.get<User>(this.createUrl('profile'))
      .pipe(
        shareReplay()
      );
  }

}